import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Role } from '../../components/auth-context/role';
import api from "../../api/api";
import airlineConfig from '../../config/airlineConfig';

const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'EN' },
    { code: 'es', name: 'ES' },
    { code: 'fr', name: 'FR' },
    { code: 'it', name: 'IT' },
    { code: 'pt', name: 'PT' },
    { code: 'ca', name: 'CA' },
    { code: 'eu', name: 'EU' },
    { code: 'gl', name: 'GL' },
];

export function RulesManager() {
    const [error, setError] = useState(null);
    const [selectedLang, setSelectedLang] = useState('en');
    const [formData, setFormData] = useState({
        title: 'Rules',
        text: '',
    });
    const [saving, setSaving] = useState(false);
    const [savedMessage, setSavedMessage] = useState('');

    useEffect(() => {
        fetchRuleForLanguage(selectedLang);
    }, [selectedLang]);

    const fetchRuleForLanguage = async (lang) => {
        try {
            const response = await api.get(`/rules/${lang}`);
            if (response?.data) {
                setFormData({
                    title: response.data.title || 'Rules',
                    text: response.data.text || '',
                });
            }
            setError(null);
        } catch (error) {
            console.error('Failed to fetch rule:', error);
            setFormData({
                title: 'Rules',
                text: '',
            });
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSavedMessage('');
        try {
            // Auto-activate if there's content, deactivate if empty
            const isActive = formData.text.trim().length > 0;
            
            await api.put(`/rules/${selectedLang}`, {
                ...formData,
                isActive
            });
            
            setSavedMessage('Rules saved successfully!');
            setTimeout(() => setSavedMessage(''), 3000);
            setError(null);
        } catch (error) {
            console.error('Failed to save rules:', error);
            const errorMessage = error.response?.data?.error || 'Failed to save rules';
            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const defaultMarkdown = `# ${airlineConfig.name} Virtual - Rules and Regulations

## 1. General Conduct

All pilots must maintain professional behavior at all times when representing ${airlineConfig.name} Virtual.

## 2. Flight Operations

### 2.1 Flight Planning
- All flights must be properly planned using appropriate charts and procedures
- SimBrief or similar flight planning tools are recommended
- Fuel planning must include appropriate reserves

### 2.2 Online Flying
- When flying on IVAO or VATSIM, follow all network rules
- Use proper callsigns (${airlineConfig.icaoCode} followed by flight number)
- Monitor appropriate frequencies

## 3. Pilot Requirements

### 3.1 Activity
- Pilots must complete at least one flight per month to remain active
- Extended absences can be requested through the vacation system

### 3.2 Training
- New pilots must complete initial training flights
- Type ratings are required for specific aircraft

## 4. Reporting

All pilots must file PIREPs (Pilot Reports) after completing flights.

## 5. Disciplinary Actions

Violations of these rules may result in:
- Warning
- Suspension
- Termination from the virtual airline

---

*These rules are subject to change. Last updated: ${new Date().toLocaleDateString()}*`;

    return (
        <div className="view-model">
            <h2>Rules Manager</h2>
            <Role has="RULE_ADMIN">
                {error && <p className="error-message">{error}</p>}
                {savedMessage && <p className="success-message">{savedMessage}</p>}
                
                {/* Language Selector */}
                <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    marginBottom: '1rem',
                    alignItems: 'center'
                }}>
                    <span style={{ marginRight: '0.5rem' }}>Language:</span>
                    {SUPPORTED_LANGUAGES.map(lang => (
                        <button
                            key={lang.code}
                            className={`btn ${selectedLang === lang.code ? 'primary' : 'secondary'}`}
                            onClick={() => setSelectedLang(lang.code)}
                            style={{ minWidth: '50px' }}
                        >
                            {lang.name}
                        </button>
                    ))}
                    
                    <button
                        className="btn success"
                        onClick={handleSave}
                        disabled={saving}
                        style={{ marginLeft: 'auto' }}
                    >
                        <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>

                {/* Editor and Preview */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {/* Editor */}
                    <div>
                        <h3>Edit Rules ({selectedLang.toUpperCase()})</h3>
                        <input
                            type="text"
                            placeholder="Title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={{ 
                                width: '100%', 
                                marginBottom: '0.5rem',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '4px',
                                backgroundColor: 'var(--background-secondary)',
                                color: 'var(--text-primary)'
                            }}
                        />
                        <textarea
                            value={formData.text}
                            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                            placeholder={defaultMarkdown}
                            style={{
                                width: '100%',
                                minHeight: '600px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                padding: '1rem',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '4px',
                                backgroundColor: 'var(--background-secondary)',
                                color: 'var(--text-primary)',
                                resize: 'vertical'
                            }}
                        />
                        <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                            {formData.text.length} / 65,535 characters
                            {formData.text.trim().length === 0 && (
                                <span style={{ color: 'var(--text-warning)', marginLeft: '1rem' }}>
                                    (Will be inactive if empty)
                                </span>
                            )}
                        </p>
                    </div>

                    {/* Preview */}
                    <div>
                        <h3>Preview</h3>
                        <div className="markdown-preview" style={{
                            padding: '1rem',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '4px',
                            backgroundColor: 'var(--background-secondary)',
                            minHeight: '650px',
                            maxHeight: '650px',
                            overflowY: 'auto'
                        }}>
                            <h2 style={{ marginTop: 0 }}>{formData.title || 'Rules'}</h2>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {formData.text || defaultMarkdown}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            </Role>
        </div>
    );
}